import { X } from '@/components/icons';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { useCrewGetPending, useCrewResendAllInvites } from '@/lib/hooks';
import { toast } from 'sonner';

interface PendingInvitesDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PendingInvitesDrawer({
  open,
  onOpenChange,
}: PendingInvitesDrawerProps) {
  const { data: pendingUsers = [] } = useCrewGetPending();
  const resendAll = useCrewResendAllInvites();

  const handleResendInvites = () => {
    resendAll.mutate(undefined, {
      onSuccess: (data) => {
        toast.success(`Sent ${data.sent} invitation${data.sent === 1 ? '' : 's'}`);
      },
      onError: () => {
        toast.error('Failed to send invitations');
      },
    });
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="bg-surface-L0 max-h-[85dvh] flex flex-col">
        <DrawerHeader className="flex justify-between items-center p-4">
          <DrawerTitle className="font-primary font-semibold text-lg text-content-primary-bold">
            Invites
          </DrawerTitle>
          <DrawerClose asChild>
            <Button variant="secondary" size="icon" roundness='round'>
              <X className="text-content-primary-subtle" />
            </Button>
          </DrawerClose>
        </DrawerHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6">
          <p className="text-sm text-content-primary-subtle mb-3">
            {pendingUsers.length} user{pendingUsers.length === 1 ? ' has' : 's have'} not logged in
          </p>

          <div className="flex flex-col">
            {pendingUsers.map((user, index) => (
              <div key={user.id}>
                {index > 0 && <Separator />}
                <div className="flex items-center justify-between py-2.5 px-2">
                  <span className="text-sm font-medium text-content-primary-bold tracking-[0.07px]">
                    {user.name || `${user.firstName} ${user.lastName}`}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {pendingUsers.length > 0 && (
            <Button
              variant='primary'
              className="w-full mt-4"
              onClick={handleResendInvites}
              disabled={resendAll.isPending}
            >
              Resend Invites
            </Button>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
